/**
 * PublicChat — client-facing chat over a project plan. No login required.
 * Opened via /public/chat/:token (signed JWT, 30d default).
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Send, Loader2, Bot, User as UserIcon, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicChat() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [identity, setIdentity] = useState({ name: "", email: "" });
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/chat/${token}/messages`);
      setMeta(data);
      setMessages(data.messages || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Link inválido");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    if (!identity.name) { alert("Please enter your name first"); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/public/chat/${token}/messages`, {
        text: input.trim(),
        author_name: identity.name,
        author_email: identity.email,
      });
      setMessages(m => [...m, data.user_message, data.ai_message]);
      setInput("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    } finally { setSending(false); }
  };

  if (error) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm" data-testid="public-chat-error">
        <div className="w-14 h-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
          <Clock size={24} />
        </div>
        <h1 className="text-xl font-semibold text-slate-900 mb-2">Link no disponible</h1>
        <p className="text-sm text-slate-600">{error}</p>
      </div>
    </div>;
  }

  if (!meta) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="animate-spin text-[#8B5CF6]" size={28} />
    </div>;
  }

  const project = meta.project || {};
  const canPost = meta.can_post;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="public-chat-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#8B5CF6] text-white flex items-center justify-center font-bold">IQ</div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">IQES · IQ-APP</div>
              <div className="font-semibold text-slate-900">{project.name || "—"}</div>
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
            {canPost ? "CHAT 2-WAY" : "READ ONLY"}
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6">
        {canPost && (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 mb-4" data-testid="public-chat-identity">
            <div className="text-xs text-slate-500 mb-2">
              Tu nombre aparecerá al lado de cada pregunta que hagas.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Nombre"
                value={identity.name}
                onChange={e => setIdentity(i => ({ ...i, name: e.target.value }))}
                data-testid="public-chat-name"
                className="border border-slate-300 rounded-xl px-3 py-2 text-sm" />
              <input placeholder="Email (opcional)" type="email"
                value={identity.email}
                onChange={e => setIdentity(i => ({ ...i, email: e.target.value }))}
                data-testid="public-chat-email"
                className="border border-slate-300 rounded-xl px-3 py-2 text-sm" />
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="max-h-[60vh] overflow-auto p-4 space-y-3" data-testid="public-chat-messages">
            {messages.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-400">
                Aún no hay preguntas sobre este plano.
              </div>
            ) : messages.map(m => {
              const isUser = m.role === "user";
              return (
                <div key={m.id}
                  data-testid={`public-chat-msg-${m.role}`}
                  className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
                  {!isUser && (
                    <div className="w-7 h-7 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] flex items-center justify-center shrink-0">
                      <Bot size={13} />
                    </div>
                  )}
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                    isUser ? "bg-[#003366] text-white" : "bg-slate-100 text-slate-800"
                  }`}>
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 mb-1">
                      {m.author_name} · {new Date(m.created_at).toLocaleString()}
                    </div>
                    <div className="whitespace-pre-wrap">{m.text}</div>
                  </div>
                  {isUser && (
                    <div className="w-7 h-7 rounded-full bg-[#003366]/10 text-[#003366] flex items-center justify-center shrink-0">
                      <UserIcon size={13} />
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          {canPost && (
            <div className="border-t border-slate-100 p-3 flex gap-2">
              <input value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && send()}
                placeholder="Pregunta sobre este plano…"
                disabled={sending}
                data-testid="public-chat-input"
                className="flex-1 border border-slate-300 rounded-xl px-3 py-2 text-sm" />
              <button onClick={send} disabled={sending || !input.trim()}
                data-testid="public-chat-send"
                className="bg-[#8B5CF6] text-white rounded-xl px-4 py-2 hover:bg-[#7C3AED] disabled:opacity-50 inline-flex items-center gap-1.5 text-sm font-medium">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Enviar
              </button>
            </div>
          )}
        </div>

        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 text-center mt-4">
          © 2026 IQES LLC · Secure signed chat · {new Date(meta.expires_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}
